import { useRouter } from 'next-nprogress-bar'
import { usePathname } from 'next/navigation'
import { FC, useContext, useEffect } from 'react'
import { loginStatusContext } from 'src/hoc/withLoginState'

import { WaitingWithData } from '../../app/_components/WaitingWithData'
import { authenticatedPaths, unauthenticatedPaths } from '../utils/route-utils'

const withAuthenticatedRoutes = (Component: FC) => {
  const MyComp = props => {
    const loginStatus = useContext(loginStatusContext)
    const router = useRouter()
    const pathname = usePathname()

    const isPublicPath = !!pathname && Object.values(unauthenticatedPaths).includes(pathname as any)
    const isNewPasswordPath = !!pathname?.includes(unauthenticatedPaths.newPasswordRequired)
    const storedRedirectPath = typeof window === 'undefined' ? null : localStorage.getItem('redirectPath')

    // Redirects are side effects. Dispatching router.push (and touching localStorage)
    // during render triggers "Cannot update a component (Router) while rendering" in
    // Next 15/React 19 and can clobber an in-flight navigation, leaving the app stuck
    // on a loading screen.
    useEffect(() => {
      if (!pathname || loginStatus === null || loginStatus === undefined || loginStatus === 'loading') return

      // If a user is logged out but is trying to access a page that isn't public, redirect
      // to the login page (new-password page excepted). Store the intended path first.
      if (loginStatus === 'loggedOut' && !isPublicPath && !isNewPasswordPath) {
        localStorage.setItem('redirectPath', pathname)
        router.push(unauthenticatedPaths.login)
        return
      }

      // If a user is logged in but is trying to access a public page, redirect to workspaces
      if (loginStatus === 'loggedIn' && isPublicPath) {
        router.push(authenticatedPaths.workspaceHome)
        return
      }

      // If localstorage has a redirect path and user is logged in, redirect to it
      if (loginStatus === 'loggedIn' && storedRedirectPath) {
        localStorage.removeItem('redirectPath')
        if (storedRedirectPath === authenticatedPaths.workspaceHome) {
          router.refresh()
        } else {
          router.push(storedRedirectPath)
        }
      }
    }, [loginStatus, pathname, isPublicPath, isNewPasswordPath, storedRedirectPath, router])

    if (loginStatus === undefined) {
      console.error(
        'withProtecteRoutes does not have the props loginStatus, and thus cannot determine if the page is protected or not. Has this HOC been moved before withLoginStatus?',
      )
      return (
        <WaitingWithData message="Login status undertermined. If problem persists, please contact your system administrator." />
      )
    }

    if (loginStatus === 'loading') {
      return <WaitingWithData message="Authentication in progress" />
    }

    if (loginStatus === 'loggedOut' && pathname && !isPublicPath) {
      if (isNewPasswordPath) {
        return <Component {...props} />
      }
      return <WaitingWithData message="Authentication in progress" />
    }

    if (loginStatus === 'loggedIn' && isPublicPath) {
      return <WaitingWithData message="Redirecting to workspaces" />
    }

    if (loginStatus === 'loggedIn' && storedRedirectPath) {
      return <WaitingWithData message="Loading" />
    }

    if (loginStatus) return <Component {...props} />
  }
  MyComp.displayName = 'withAuthenticatedRoutes'
  return MyComp
}

export default withAuthenticatedRoutes
